/**
 * System-run daily actions rendered as their own box directly under the AI Spotlight box
 * (e.g. "7 SKUs to send to Amazon FBA today") — one full-width card per action, no section
 * header. Same typography hierarchy as AISpotlightCard: label 10px uppercase muted →
 * heading 15px semibold → action 12px muted.
 *
 * User-created alerts stay in Pending Actions; only system action alert types show here.
 */

import { Badge, Box, Group, Skeleton, Text } from '@mantine/core';
import { CaretRight, Lightning } from '@phosphor-icons/react';
import { memo, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import DeleteAlertButton from './DeleteAlertButton';

import { fetchAlertsByCategory } from '@components/TodoChat/api';
import type { AlertItem } from '@components/TodoChat/types';
import { GeneratedBy } from '@components/TodoChat/types';
import { buildAlertCta, SYSTEM_ACTION_ALERT_TYPES, sumRecommendedUnits } from '@components/TodoChat/utils/alert-cta';

import type { AISpotlightSystemActionsConfig } from '../../types';

import classes from './SystemActionCards.module.css';

const formatRunTime = (iso?: string): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

interface CardProps {
    alert: AlertItem;
    detailPath: string;
}

/** Semantic colour for an artifact outcome: done is teal, undone is red, changed is amber. */
function outcomeColor(state: string): string {
    switch (state) {
        case 'APPROVED':
        case 'PUSHED':
            return 'teal';
        case 'DELETED':
            return 'red';
        case 'EDITED':
        case 'RECOMPUTED':
            return 'yellow';
        default:
            return 'gray';
    }
}

const SystemActionCard: React.FC<CardProps> = memo(({ alert, detailPath }) => {
    const navigate = useNavigate();
    // Removed from Home by its trash icon: the alert row is gone, so the card goes with it.
    const [removed, setRemoved] = useState(false);
    const cta = useMemo(() => buildAlertCta(alert), [alert]);
    const units = useMemo(() => sumRecommendedUnits(alert), [alert]);
    const runTime = formatRunTime(alert.updatedAt || alert.alertActiveFrom);
    const outcomeTime = alert.outcome ? formatRunTime(alert.outcome.at) : null;
    const detailHref = detailPath.replace(':id', alert.id);

    const meta = [
        units > 0 ? `${units.toLocaleString('en-IN')} units` : null,
        runTime ? `computed ${runTime}` : null,
    ]
        .filter(Boolean)
        .join(' · ');

    if (removed) return null;

    return (
        <Box
            className={`group ${classes.card}`}
            onClick={() => void navigate(cta ? cta.href : detailHref)}
            role="button"
            tabIndex={0}
        >
            <Group justify="space-between" align="center" wrap="nowrap" gap={24}>
                <Box className={classes.body}>
                    {/* Label: 10px uppercase muted */}
                    <Group gap={4} wrap="nowrap">
                        <Lightning size={12} weight="fill" className={classes.labelIcon} />
                        <Text className={classes.label}>{alert.alertTypeLabel}</Text>
                    </Group>

                    {/* Heading: 15px semibold */}
                    <Text className={classes.heading}>{alert.title}</Text>

                    {/* Secondary: 12px muted */}
                    {meta && <Text className={classes.meta}>{meta}</Text>}

                    {/* Feedback from the artifact: what was done with it since the run. */}
                    {alert.outcome && (
                        <Group gap={6} wrap="nowrap" mt={4}>
                            <Badge size="xs" variant="light" color={outcomeColor(alert.outcome.state)}>
                                {alert.outcome.state}
                            </Badge>
                            <Text className={classes.meta}>
                                {alert.outcome.summary}
                                {outcomeTime ? ` · ${outcomeTime}` : ''}
                            </Text>
                        </Group>
                    )}
                </Box>

                {/* Actions: 12px muted, primary + details */}
                <Group gap={16} wrap="nowrap" className={classes.actions}>
                    <DeleteAlertButton alert={alert} onDeleted={() => setRemoved(true)} />
                    {/* An explicit redirect (artifact) is the card's only destination; the detail page
                        stays for the hand-wired types that have no link of their own. */}
                    {cta && !alert.artifact && (
                        <Text
                            className={classes.actionSecondary}
                            onClick={(e) => {
                                e.stopPropagation();
                                void navigate(detailHref);
                            }}
                        >
                            View SKUs
                        </Text>
                    )}
                    <Group gap={4} wrap="nowrap" className={classes.actionPrimary}>
                        <Text className={classes.actionText}>
                            {cta ? cta.label : 'Open'}
                        </Text>
                        <CaretRight size={12} weight="bold" className={classes.actionCaret} />
                    </Group>
                </Group>
            </Group>
        </Box>
    );
});

SystemActionCard.displayName = 'SystemActionCard';

interface SystemActionCardsProps {
    config: AISpotlightSystemActionsConfig;
}

/**
 * Renders nothing while loading with no prior data and nothing when there are no open system
 * actions, so the spotlight keeps its 3-card shape on quiet days.
 */
const SystemActionCards: React.FC<SystemActionCardsProps> = memo(({ config }) => {
    const [alerts, setAlerts] = useState<AlertItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const allowedTypes = useMemo(
        () => new Set(config.alertTypes ?? [...SYSTEM_ACTION_ALERT_TYPES]),
        [config.alertTypes],
    );

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        fetchAlertsByCategory(GeneratedBy.SYSTEM)
            .then((categorized) => {
                if (cancelled) return;
                setAlerts(categorized.pending.filter((a) => allowedTypes.has(a.alertType as string)));
            })
            .catch(() => {
                if (!cancelled) setAlerts([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [allowedTypes]);

    if (isLoading) {
        return (
            <Box className={classes.card}>
                <Skeleton height={10} width="20%" radius={2} />
                <Skeleton height={18} width="55%" radius={2} mt={6} />
                <Skeleton height={12} width="30%" radius={2} mt={6} />
            </Box>
        );
    }
    if (alerts.length === 0) return null;

    return (
        <Box className={classes.stack}>
            {alerts.map((a) => (
                <SystemActionCard key={a.id} alert={a} detailPath={config.detailPath} />
            ))}
        </Box>
    );
});

SystemActionCards.displayName = 'SystemActionCards';

export default SystemActionCards;
